// Plano 02/10/2026 (Frente 13): ClassesIds sai do modelo genérico PaginaCadastroGeral e passa ao
// padrão visual do Cadastro de Projetistas (pages/Projetistas): cabeçalho, botão Cadastrar, grid
// listrada e modal próprio. Campos inalterados (Código e Descrição, decisões de 22/09); mesmos
// endpoints de /ClasseId.
import { useEffect, useMemo, useState } from "react";
import { Grid, Paper } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { debounce } from "lodash";
import NavigationHeader from "components/NavigationHeader";
import { StripedDataGrid } from "components/StripedDataGrid";
import { FloatMenuCrud } from "components/FloatMenuCrud";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import { TitleTable } from "components/TitleTable";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import { DashboardStackCustom, StackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import { TIME_DEBOUNCE } from "hooks/usePagination";
import { excluirCadastro, listarCadastro } from "services/modules/cadastrosGerais";
import { IClasseId, ModalClasseId } from "./ModalClasseId";

export const ClassesIds = () => {
  const { openNotification } = useNavigation();
  const [classesIds, setClassesIds] = useState<IClasseId[]>([]);
  const [totalItens, setTotalItens] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [paginacao, setPaginacao] = useState({ page: 1, pageSize: 10, search: "" });

  const [modal, setModal] = useState<{ aberto: boolean; dados?: IClasseId }>({ aberto: false });
  const [modalExclusao, setModalExclusao] = useState<{ aberto: boolean; id?: number }>({ aberto: false });

  const carregar = async (params = paginacao) => {
    setCarregando(true);
    try {
      const pagina = await listarCadastro<IClasseId>("/ClasseId", params);
      setClassesIds(pagina.itens);
      setTotalItens(pagina.total);
    } catch (error: any) {
      openNotification({ message: error?.message || "Não foi possível carregar as ClassesIds", type: "error" });
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar(paginacao);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginacao]);

  const filtrar = useMemo(
    () => debounce((texto: string) => setPaginacao((atual) => ({ ...atual, page: 1, search: texto })), TIME_DEBOUNCE),
    []
  );

  const excluir = async (id?: number) => {
    if (!id) return;
    setExcluindo(true);
    try {
      await excluirCadastro("/ClasseId", "idClasseId", id);
      openNotification({ message: "ClasseId excluída com sucesso!", type: "success" });
      await carregar(paginacao);
    } catch (error: any) {
      openNotification({ message: error?.message || "Erro ao excluir ClasseId", type: "error" });
    } finally {
      setExcluindo(false);
      setModalExclusao({ aberto: false });
    }
  };

  const colunas: GridColDef[] = [
    {
      field: "codigo",
      headerName: "Código",
      flex: 0.5,
      sortable: false,
      renderHeader: () => (
        <FilterColumn label="Código" name="codigo" onChange={(evento) => filtrar(evento.target.value)} />
      ),
    },
    {
      field: "descricao",
      headerName: "Descrição",
      flex: 1,
      sortable: false,
    },
    {
      field: "options",
      sortable: false,
      headerName: "Ações",
      align: "center",
      headerAlign: "center",
      flex: 0.15,
      renderCell: (params) => (
        <FloatMenuCrud
          onEdit={() => setModal({ aberto: true, dados: params.row })}
          showDuplicateOption={false}
          deleteButtonText="Excluir"
          onDelete={() => setModalExclusao({ aberto: true, id: params.row.idClasseId })}
        />
      ),
    },
  ];

  return (
    <DashboardStackCustom>
      <NavigationHeader
        title="CADASTRO DE CLASSESIDS"
        label="ClassesIds"
        buttonLabel="Cadastrar"
        buttonOnClick={() => setModal({ aberto: true })}
        hasBackButton={false}
      />
      <Grid container display="flex" alignItems="center">
        <StackCustom>
          <Grid container mb={4}>
            <Grid item xs={12} sm={8} md={12}>
              <TitleTable title="Listagem de classesids do MOF" />
            </Grid>
          </Grid>
          <Paper>
            <StripedDataGrid
              rows={classesIds}
              columns={colunas}
              getRowId={(linha: IClasseId) => linha.idClasseId}
              page={paginacao.page - 1}
              pageSize={paginacao.pageSize}
              rowCount={totalItens}
              loading={carregando || excluindo}
              onPageChange={(pagina) => setPaginacao((atual) => ({ ...atual, page: pagina + 1 }))}
              onPageSizeChange={(tamanho) => setPaginacao((atual) => ({ ...atual, pageSize: tamanho, page: 1 }))}
              checkboxSelection={false}
              rowHeight={70}
            />
          </Paper>
        </StackCustom>
      </Grid>

      <ModalClasseId
        isOpen={modal.aberto}
        onClose={() => setModal({ aberto: false })}
        refreshData={() => carregar(paginacao)}
        dataClasseId={modal.dados}
      />

      <ConfirmDeleteModal
        title="Tem certeza que deseja excluir a ClasseId?"
        description="Essa operação não pode ser desfeita."
        open={modalExclusao.aberto}
        onClose={() => setModalExclusao({ aberto: false })}
        onConfirm={() => excluir(modalExclusao.id)}
        isLoadingDelete={excluindo}
      />
    </DashboardStackCustom>
  );
};
